import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LoadingButton } from '@mui/lab';
import {
	AppBar,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	TextField,
	Toolbar,
	Typography
} from '@mui/material';
import { DialogTransition, FollowUpHistory } from 'components';
import { animated, useTransition } from '@react-spring/web';
import { useScreenType } from 'hooks';
import { EActionState } from 'models';
import { ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actionCreator, selectors } from 'store/followups/update';
import { theme } from 'theme';
import { pacsService } from 'services';

export const UpdateFollowUpDialog = () => {
	const dispatch = useDispatch();
	const loading = useSelector(selectors.getInitAction);
	const open = useSelector(selectors.getOpen);
	const screenType = useScreenType();

	// Form state from Redux
	const followUpResult = useSelector(selectors.form.getFollowUpResult);

	const handleCancel = () => {
		dispatch(actionCreator.INIT_FLUSH());
	};

	// Validation
	const fieldErrors = {
		followUpResult: pacsService.FollowUpUpdateNotesRequired && followUpResult.newValue.trim().length === 0
	};
	const isFormValid = !fieldErrors.followUpResult;

	return (
		<Dialog
			open={open}
			fullScreen={screenType.isPhone}
			onClose={(event, reason) => {
				if (reason === 'backdropClick') return;
				handleCancel();
			}}
			fullWidth={true}
			maxWidth={'md'}
			TransitionComponent={DialogTransition}
			transitionDuration={{
				appear: theme.transitions.duration.enteringScreen,
				enter: theme.transitions.duration.enteringScreen,
				exit: 0
			}}>
			{screenType.isPhone && <TitleBar title='Update Follow Up' onClose={handleCancel} />}
			{screenType.isDesktop && <DialogTitle>Update Follow Up</DialogTitle>}
			<DialogContent sx={{ p: 0, minHeight: 420, overflow: 'hidden' }}>
				{loading === EActionState.RUNNING && (
					<Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
						<CircularProgress />
					</Box>
				)}
				{(() => {
					const show = loading === EActionState.COMPLETED;
					const transitions = useTransition(show, {
						from: { opacity: 0, transform: 'translateY(6px)' },
						enter: { opacity: 1, transform: 'translateY(0px)' },
						leave: { opacity: 0, transform: 'translateY(6px)' },
						config: { tension: 200, friction: 20 }
					});
					return transitions(
						(styles, item) =>
							item && (
								<animated.div style={{ ...styles, willChange: 'opacity, transform', display: 'block' }}>
									<Box sx={{ width: '100%', p: 2 }}>
										<Content fieldErrors={fieldErrors} />
									</Box>
								</animated.div>
							)
					);
				})()}
			</DialogContent>
			<DialogActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
				{screenType.isDesktop ? (
					<>
						<CloseOpportunityButton isFormValid={isFormValid} />
						<Box sx={{ display: 'flex', gap: 1 }}>
							<ScheduleNextButton isFormValid={isFormValid} />
							<Button color='neutral' variant='outlined' onClick={handleCancel}>
								Cancel
							</Button>
						</Box>
					</>
				) : (
					<Stack spacing={1} sx={{ flex: '1 1 0px' }}>
						<CloseOpportunityButton isFormValid={isFormValid} />
						<ScheduleNextButton isFormValid={isFormValid} />
						<Box sx={{ height: 16 }} />
						<Button color='neutral' variant='outlined' onClick={handleCancel}>
							Cancel
						</Button>
					</Stack>
				)}
			</DialogActions>
		</Dialog>
	);
};

interface ActionButtonProps {
	isFormValid: boolean;
}

const CloseOpportunityButton = (props: ActionButtonProps) => {
	const { isFormValid } = props;
	const dispatch = useDispatch();
	const updateAction = useSelector(selectors.getUpdateAction);
	const screenType = useScreenType();

	const handleClick = () => {
		dispatch(actionCreator.UPDATE_START({ scheduleNext: false }));
	};

	return (
		<LoadingButton
			color='error'
			variant='contained'
			onClick={handleClick}
			disabled={!isFormValid}
			loading={updateAction === EActionState.RUNNING}
			sx={{ flex: screenType.isPhone ? '1 1 0px' : 'inherit' }}>
			Complete and Close Opportunity
		</LoadingButton>
	);
};

const ScheduleNextButton = (props: ActionButtonProps) => {
	const { isFormValid } = props;
	const dispatch = useDispatch();
	const updateAction = useSelector(selectors.getUpdateAction);
	const screenType = useScreenType();

	const handleClick = () => {
		dispatch(actionCreator.UPDATE_START({ scheduleNext: true }));
	};

	return (
		<LoadingButton
			variant='outlined'
			onClick={handleClick}
			disabled={!isFormValid}
			loading={updateAction === EActionState.RUNNING}
			sx={{ flex: screenType.isPhone ? '1 1 0px' : 'inherit' }}>
			Complete and Schedule Next Follow Up
		</LoadingButton>
	);
};

const TitleBar = (props: { title: string; onClose: () => void }) => {
	const { title, onClose } = props;
	return (
		<AppBar position='static' color='inherit'>
			<Toolbar>
				<IconButton color='inherit' onClick={onClose}>
					<ArrowBackIcon />
				</IconButton>
				<Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
					{title}
				</Typography>
			</Toolbar>
		</AppBar>
	);
};

interface ContentProps {
	fieldErrors: {
		followUpResult: boolean;
	};
}

const Content = (props: ContentProps) => {
	const { fieldErrors } = props;
	const dispatch = useDispatch();
	const followUpResult = useSelector(selectors.form.getFollowUpResult);
	const followUps = useSelector(selectors.getFollowUps);
	const followUpId = useSelector(selectors.getFollowUpId);

	const handleResultChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
		const value = event.target.value;
		if (value.length <= 500) {
			dispatch(actionCreator.SET_FOLLOW_UP_RESULT(value));
		}
	};

	return (
		<Box sx={{ p: 2, pt: 0 }}>
			<FollowUpHistory items={followUps} sx={{ pb: 3 }} />
			<Typography variant='subtitle1' sx={{ pb: 1, fontWeight: 'bold' }}>
				{followUpId}
			</Typography>
			<Stack spacing={1}>
				<TextField
					label='Follow Up Result'
					multiline
					rows={8}
					fullWidth
					value={followUpResult.newValue}
					onChange={handleResultChange}
					inputProps={{ maxLength: 250 }}
					error={fieldErrors.followUpResult}
					helperText={fieldErrors.followUpResult ? 'Required' : ''}
				/>
				<Typography variant='caption' sx={{ textAlign: 'right', color: 'text.secondary' }}>
					{followUpResult.newValue.length} / 250
				</Typography>
			</Stack>
		</Box>
	);
};
